import { ReactElement } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface ElevationProfilePlotProps {
  route: any;
  gates?: any;
  height?: number;
}

export default function ElevationProfilePlot({ route, gates, height = 300 }: ElevationProfilePlotProps): ReactElement {
  if (!route || !route.route_beta || !Array.isArray(route.route_beta)) {
    return <div className="cr-p-4 cr-text-gray-500">No elevation data available</div>;
  }

  // Find gate positions on the route
  const findClosestRouteIndex = (gateLat: number, gateLon: number): number => {
    if (!route.route_lat || !route.route_lon) return -1;

    let minDistance = Infinity;
    let closestIndex = 0;

    for (let i = 0; i < route.route_lat.length; i++) {
      const lat = route.route_lat[i];
      const lon = route.route_lon[i];
      const distance = Math.sqrt(Math.pow(lat - gateLat, 2) + Math.pow(lon - gateLon, 2));
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }

    return closestIndex;
  };

  // Find Gate A position and get its beta value
  let gateABeta = 0;
  if (gates?.gates_lat && gates?.gates_lon) {
    const gateAIndex = findClosestRouteIndex(gates.gates_lat[0], gates.gates_lon[0]);
    if (gateAIndex >= 0 && gateAIndex < route.route_beta.length) {
      gateABeta = route.route_beta[gateAIndex];
    }
  }

  // Calculate elevation as (route_beta - gateABeta) / -9.8
  const elevationData = route.route_beta.map((beta: number, index: number) => ({
    x: route.route_pos?.[index] ?? index,
    y: (beta - gateABeta) / -9.8,
  }));

  const annotations: any[] = [];
  if (gates?.gates_lat && gates?.gates_lon) {
    const gateAIndex = findClosestRouteIndex(gates.gates_lat[0], gates.gates_lon[0]);
    const gateBIndex = findClosestRouteIndex(gates.gates_lat[1], gates.gates_lon[1]);

    if (gateAIndex >= 0) {
      const xPos = route.route_pos?.[gateAIndex] ?? gateAIndex;
      annotations.push({
        x: xPos,
        borderColor: '#BBD5DE',
        label: {
          borderColor: '#BBD5DE',
          style: {
            color: '#03365D',
            background: '#BBD5DE',
          },
          text: 'Gate A',
        },
      });
    }

    if (gateBIndex >= 0) {
      const xPos = route.route_pos?.[gateBIndex] ?? gateBIndex;
      annotations.push({
        x: xPos,
        borderColor: '#03365D',
        label: {
          borderColor: '#03365D',
          style: {
            color: '#fff',
            background: '#03365D',
          },
          text: 'Gate B',
        },
      });
    }
  }

  const options: ApexOptions = {
    chart: {
      type: 'scatter',
      toolbar: {
        show: true,
      },
      zoom: {
        enabled: true,
        allowMouseWheelZoom: false,
      },
    },
    xaxis: {
      title: {
        text: route.route_pos ? 'Position (m)' : 'Index',
      },
      decimalsInFloat: 1,
    },
    yaxis: {
      title: {
        text: 'Elevation (m)',
      },
      decimalsInFloat: 1,
      labels: {
        formatter: (value: number) => value.toFixed(1),
      },
    },
    markers: {
      size: 4,
    },
    colors: ['#1879B9'],
    title: {
      text: 'Route Elevation Profile',
      align: 'center',
    },
    tooltip: {
      y: {
        formatter: (value: number) => value.toFixed(1) + ' m',
      },
    },
    annotations: {
      xaxis: annotations,
    },
  };

  const series = [
    {
      name: 'Elevation',
      data: elevationData,
    },
  ];

  return (
    <div className="cr-rounded-xl cr-bg-clip-border cr-shadow-md cr-p-4" style={{ height: `${height}px` }}>
      <Chart options={options} series={series} type="scatter" height="100%" />
    </div>
  );
}
